import type { PaneInit } from "@hq/layout";

/** Every pane type the workspace can show. `layout` never knows these; the registry maps them. */
export type PaneSpec =
  | { paneType: "today" }
  | { paneType: "inbox" }
  | { paneType: "anytime" }
  | { paneType: "upcoming" }
  | { paneType: "someday" }
  | { paneType: "completed" }
  | { paneType: "project"; projectId: string }
  | { paneType: "daily" }
  | { paneType: "allNotes" }
  | { paneType: "folder"; folderId: string }
  | { paneType: "templates" }
  | { paneType: "note"; noteId: string; autoFocus?: boolean }
  | { paneType: "task"; taskId: string }
  | { paneType: "calendar" }
  | { paneType: "dashboards" }
  | { paneType: "trash" }
  | { paneType: "settings" };

export type PaneType = PaneSpec["paneType"];

export const PANE_TITLES: Record<
  Exclude<PaneType, "project" | "note" | "task" | "folder">,
  string
> = {
  today: "Today",
  inbox: "Inbox",
  anytime: "Anytime",
  upcoming: "Upcoming",
  someday: "Someday",
  completed: "Completed",
  daily: "Daily Notes",
  allNotes: "All Notes",
  templates: "Templates",
  calendar: "Calendar",
  dashboards: "Dashboards",
  trash: "Trash",
  settings: "Settings",
};

/** Params that identify a pane for dedupe. Transient flags are added by the workspace model. */
export function paneInit(spec: PaneSpec): PaneInit {
  switch (spec.paneType) {
    case "project":
      return { paneType: "project", params: { projectId: spec.projectId } };
    case "folder":
      return { paneType: "folder", params: { folderId: spec.folderId } };
    case "note":
      return { paneType: "note", params: { noteId: spec.noteId } };
    case "task":
      return { paneType: "task", params: { taskId: spec.taskId } };
    default:
      return { paneType: spec.paneType, params: {}, title: PANE_TITLES[spec.paneType] };
  }
}

/** The URL for a pane, so the address bar follows focus and deep links open panes. */
export function routeFor(paneType: string, params: Record<string, unknown>): string {
  switch (paneType) {
    case "project":
      return `/projects/${String(params.projectId)}`;
    case "folder":
      return `/folders/${String(params.folderId)}`;
    case "note":
      return `/notes/${String(params.noteId)}`;
    case "task":
      return `/tasks/${String(params.taskId)}`;
    case "allNotes":
      return "/notes";
    default:
      return `/${paneType}`;
  }
}
